import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import cn from 'classnames';

import useCloseOverlay from '@/hooks/useCloseOverlay';
import ChevronDown from '@/icons/ChevronDown';
import ButtonCircle from '@/components/ButtonCircle';
import HelpWidget from '@/components/HelpWidget';

import { type INavItem } from '@/components/Header/Header.types';
import { type MegaNavProps } from './MegaNav.types';

const MegaNav: React.FC<MegaNavProps> = ({
  onClickOutside,
  page,
  helpPageUrl,
  helpPageText,
  transition,
}) => {
  const {
    children: pageChildren,
    title: pageTitle,
    url: pageUrl,
    helpLinks,
  } = page;

  const [pageId, setPageId] = useState<string>(undefined);
  const [childData, setChildData] = useState<INavItem>(undefined);
  const hasChildren = pageChildren && pageChildren.length > 0;
  const hasSubChildren = childData && childData.children.length > 0;

  // common props for the button arrows
  const arrowFill = '#FFFFFF';
  const arrowHeight = 17;
  const arrowWidth = 21;

  // setup click handler for when mega nav is open, to be able to close it when
  // the user clicks outside of it
  useCloseOverlay(['mega-nav', 'nav__list'], onClickOutside);

  // when the page changes, update the child data
  useEffect(() => {
    if (hasChildren) {
      const data = pageChildren.find((child) => child.id === pageId);
      setChildData(data);
    }
    return () => setChildData(undefined);
  }, [pageId, page]);

  return (
    <motion.div
      className="mega-nav"
      key="mega-nav"
      initial={{ opacity: 0.9, y: '-100%' }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0.9, y: '-100%' }}
      transition={transition}
      style={{ zIndex: 2 }} // dropdown layering
    >
      <div className="mega-nav__section mega-nav__section--left">
        <a
          className="mega-nav__page-link h3 button--secondary"
          href={pageUrl}
          onClick={() => {
            if (window.dataLayer) {
              window.dataLayer.push({
                event: 'navigation_widget',
                group: pageTitle,
                subgroup: 'null',
              });
            }
          }}
        >
          <span>{pageTitle}</span>
          <ButtonCircle
            arrowFill={arrowFill}
            arrowHeight={arrowHeight}
            arrowWidth={arrowWidth}
          />
        </a>
        <nav className="mega-nav__primary-nav">
          {hasChildren && (
            <ul className="mega-nav__list">
              {pageChildren.map((child, i) => {
                const { id, title: childTitle, children, linkingMode } = child;
                const hasSubChildren = children && children.length > 0;

                return (
                  <li className="mega-nav__list-item" key={i}>
                    {hasSubChildren ? (
                      <button
                        type="button"
                        className={cn('mega-nav__list-button', {
                          'mega-nav__list-button--active': pageId === id,
                        })}
                        data-testid={`MegaNav__PrimaryNavButton--${i + 1}`}
                        onClick={() => {
                          if (window.dataLayer) {
                            window.dataLayer.push({
                              event: 'navigation_widget',
                              group: pageTitle,
                              subgroup: childTitle,
                            });
                          }

                          setPageId(pageId && pageId === id ? undefined : id);
                        }}
                      >
                        {childTitle}

                        <motion.div
                          layout
                          initial={{ rotate: -90 }}
                          animate={{ scale: pageId === id ? -1 : 1 }}
                          transition={{ ease: 'easeInOut' }}
                        >
                          <ChevronDown fill="currentColor" width={11} />
                        </motion.div>
                      </button>
                    ) : (
                      <a
                        href={child.url}
                        className={`mega-nav__list-link mega-nav__list-link--${linkingMode}`}
                        target={child.isNewWindow && '_blank'}
                      >
                        {child.title}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </nav>
      </div>

      <div className="mega-nav__section mega-nav__section--right">
        <AnimatePresence mode="wait">
          {childData && pageId !== undefined && (
            <motion.nav
              className="mega-nav__secondary-nav"
              initial={{ opacity: 0, x: '-20%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '-20%' }}
              transition={{ bounce: 0 }}
            >
              <a
                className="mega-nav__secondary-nav-title"
                href={childData.url}
                target={childData.isNewWindow && '_blank'}
                onClick={() => {
                  if (window.dataLayer) {
                    window.dataLayer.push({
                      event: 'navigation_widget',
                      group: pageTitle,
                      subgroup: childData.title,
                    });
                  }
                }}
              >
                <span>{childData.title}</span>
              </a>
              {hasSubChildren && (
                <ul
                  className={cn('mega-nav__list', 'mega-nav__list--secondary', {
                    'mega-nav__list--wide': !helpLinks || !helpLinks.length,
                  })}
                >
                  {childData.children.map((subChild, i) => {
                    const { title, url, isNewWindow, linkingMode } = subChild;

                    return (
                      <li className="mega-nav__list-item" key={i}>
                        <a
                          className={`mega-nav__list-link mega-nav__list-link--${linkingMode}`}
                          href={url}
                          target={isNewWindow && '_blank'}
                          onClick={() => {
                            if (window.dataLayer) {
                              window.dataLayer.push({
                                event: 'navigation_widget',
                                group: pageTitle,
                                subgroup: `${childData.title} -> ${title}`,
                              });
                            }
                          }}
                        >
                          {title}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </motion.nav>
          )}
        </AnimatePresence>

        {helpLinks && (
          <HelpWidget
            helpPageText={helpPageText}
            helpPageUrl={helpPageUrl}
            helpLinks={helpLinks}
          />
        )}
      </div>
    </motion.div>
  );
};

export default MegaNav;
